import { useEffect } from 'react'
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router'
import { z } from 'zod'
import type { EditorView, OrderView } from '@hedgehog/shared'
import { api } from '@/lib/api'
import { AppShell } from '@/components/app/app-shell'
import { ADMIN_SECTIONS, AdminScreen } from '@/components/app/editor/admin-screen'
import { OrderError, startSession } from '@/components/app/order'
import { LoadingState } from '@/components/app/states'
import { buttonVariants } from '@/components/ui/button'

// The admin full screen, outside the account layout, with the same access as the editor.

export const Route = createFileRoute('/o_/$orderId/admin')({
  validateSearch: z.object({ t: z.string().optional().catch(undefined), section: z.enum(ADMIN_SECTIONS).optional().catch(undefined) }),
  loaderDeps: ({ search }) => ({ t: search.t }),
  loader: async ({ params, deps, abortController }) => {
    if (deps.t) await startSession(params.orderId, deps.t, abortController.signal)
    const id = encodeURIComponent(params.orderId)
    const [order, view] = await Promise.all([
      api<OrderView>(`/orders/${id}`, { signal: abortController.signal }),
      api<EditorView>(`/orders/${id}/editor`, { signal: abortController.signal }),
    ])
    return { order, view }
  },
  head: ({ loaderData }) => ({
    meta: [
      { title: loaderData ? `Admin · ${loaderData.order.businessName} · Hedgehog` : 'Admin · Hedgehog' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  pendingComponent: () => <LoadingState label="Opening the admin" className="h-dvh" />,
  errorComponent: ({ error }) => (
    <AppShell>
      <OrderError error={error} title="We could not open the admin" />
    </AppShell>
  ),
  component: FullScreenAdmin,
})

function FullScreenAdmin() {
  const { order, view } = Route.useLoaderData()
  const { t, section } = Route.useSearch()
  const navigate = useNavigate()

  // Drop the access token from the address bar once the session cookie is set.
  useEffect(() => {
    if (t) void navigate({ to: '.', search: (s: { section?: (typeof ADMIN_SECTIONS)[number] }) => ({ section: s.section }), replace: true })
  }, [t, navigate])

  if (view.status !== 'ready') {
    return (
      <AppShell>
        <div className="flex max-w-xl flex-col items-start gap-3 py-8">
          <h1 className="text-xl font-semibold">The admin is not ready yet</h1>
          <p className="text-muted-foreground">Your account shows when it is ready, and how to reach us in the meantime.</p>
          <Link to="/o/$orderId/editor" params={{ orderId: order.id }} className={buttonVariants({ variant: 'outline' })}>
            Back to your account
          </Link>
        </div>
      </AppShell>
    )
  }
  return <AdminScreen orderId={order.id} businessName={order.businessName} blog={!!order.site?.features.includes('blog')} section={section ?? 'pages'} />
}
